<template>
  <div id="app">
    <router-view v-slot="{ Component }">
      <Transition  :name="transitionName" >
        <Component :is="Component" />
      </Transition> 
    </router-view>
    <PlayCanvasComponent/>
    <AccessoryPopup
      :accName="$store.state.accessoryPopup.accName"
      :showPopup="$store.state.accessoryPopup.show"
      :isIncluded="$store.state.accessoryPopup.isIncluded"/>
    <AccessoryConflict
      :notice="$store.state.accessoryConflict.notice"
      :question="$store.state.accessoryConflict.question"
      :showPopup="$store.state.accessoryConflict.show"/>
    <FinishingOptionPopup/>
    <SimpleInfoPopup/>
    <OptionMenuFooter/>
    <ZetaGlobalScript />
    <div id="form-debug">
      <h1>FORM DEBUG ACTIVE</h1>
      <p id="form-debug-output"></p>
    </div>
  </div>
</template>
<script>
import PlayCanvasComponent from './components/PlayCanvas'
import PlayCanvas from './playcanvas.js'
import AccessoryPopup from './components/accessories/AccessoryPopup'
import AccessoryConflict from './components/accessories/AccessoryConflict'
import OptionMenuFooter from './components/options/OptionMenuFooter'
import {getModelByHandle} from './poolData'
import FinishingOptionPopup from './components/options/FinishingOptionPopup.vue'
import { mapGetters } from 'vuex'
import SimpleInfoPopup from './components/SimpleInfoPopup.vue'
import ZetaGlobalScript from './components/ZetaGlobalScript.vue'
import { breakpoint } from './styles/style';

window.formDebug = false;

export default {
  name: 'App',
  components: {
    PlayCanvasComponent,
    AccessoryPopup,
    AccessoryConflict,
    OptionMenuFooter,
    FinishingOptionPopup,
    SimpleInfoPopup,
    ZetaGlobalScript,
  },
  data() {
    return {
      transitionName: 'cust-slide-left',
    };
  },
  computed:{
    ...mapGetters(['currPool']),
  },
  created() {

    // Pool selector + customizer transitions
    this.$router.beforeEach((to, from) => {

      this.transitionName = 'cust-slide-right';

      if (to.path == '/summary' || (to.path == '/customizer' && from.path == '/summary')) {
        this.transitionName = 'fade';
        
      } else if (to.path == '/customizer') {
        this.transitionName = 'cust-slide-left';
      }
    });

    // Cascade any dimension updates inside PlayCanvas back to UI to stay in sync
    const dimensionsUpdate = e => {
      let newDimensions = e.detail;
      if (window.logging) console.log('VUE.JS - UPDATE DIMENSIONS WITH:', newDimensions);

      this.$store.commit('setLength', newDimensions.len + '');
      this.$store.commit('setWidth', newDimensions.width + '');
      this.$store.commit('setDepth', newDimensions.depth + '');
    };
    window.addEventListener('vue:newDimensions', dimensionsUpdate);
  },
  mounted() {
    PlayCanvas.startLoading();
    // Check for URL params to open a specific pool
    let params = new URLSearchParams(new URL(window.location).search);
    let modelFound;
    if (params.has('model')) {
      let modelParam = params.get('model');
      modelFound = getModelByHandle(modelParam);

      if (modelFound) {
        const loadModel = () => {
          if (window.pc && window.pc.Application && window.pc.Application.getApplication()) {
            this.$store.dispatch('setModel', modelFound);
            /* 
              catch() call here silences NavigationDuplicated error that appearing in console when using URL with model identifier
              solution discussed here and at linked github issue: 
              https://stackoverflow.com/questions/57837758/navigationduplicated-navigating-to-current-location-search-is-not-allowed 
            */
            this.$router.push('/customizer').catch(() => {});
          } else {
            setTimeout(loadModel, 250);
          }
        };
        loadModel();
      } else {
        console.warn('Could not find model from url param with value: ', modelParam);
        // people may use an old URL with a model ID for pool that has been removed. 
        // direct them to the pool selection page.
        this.$router.push('/select-pool');
      }
    } 
    
    // Form debugging
    if (params.has('form-debug')) {
      window.formDebug = true;
      document.querySelector('#form-debug').style.display = 'block';
    }
    
    // used by the optioncontainer component
    window.addEventListener('resize', () => {
      if (window.innerWidth > breakpoint.xl) {
        // set options menu not collapsed if switching to desktop UI
        this.$store.commit('setCollapsed', false);
      } else {
        // set options menu collapsed if resizing window (switching orientations) on mobile, to avoid obstructing 3d view 
        this.$store.commit('setCollapsed', true);
      }
    });
  },
}
</script>
<style lang="scss">

#app {
  height: 100%;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  // Form debugging
  #form-debug {
    display: none; // By default don't show unless ?form-debug param present
    position: fixed;
    z-index: 9999;
    top: 9em;
    left: 4em;
    max-width: 30em;
    padding: 2em;
    background-color: darkred;
    border: 1px solid red;
    opacity: .7;
    h1 {
      color: white;
      font-weight: bold;
      font-size: 1em;
      font-family: Arial;
      letter-spacing: 3px;
    }
    p {
      color: white;
      font-size: 0.7rem;
      font-family: Arial;
      font-weight: bold;
      padding: 2em 1em;
    }
  }
}
// Sliding animations
.cust-slide-left-enter-active,
.cust-slide-right-enter-active {
  transition-property: opacity, transform;
  transition-timing-function: cubic-bezier(0.215, 0.610, 0.355, 1);
  overflow: hidden;
}
.cust-slide-left-enter-active {
  transition-duration: 0ms;
}
.cust-slide-right-enter-active {
  transition-duration: 300ms;
  @media #{ $desktop } {
    transition-duration: 500ms;
  }
}
.cust-slide-left-leave-active,
.cust-slide-right-leave-active {
  transition-duration: 300ms;
  transition-property: opacity, transform;
  transition-timing-function: cubic-bezier(0.215, 0.610, 0.355, 1);
  overflow: hidden;
  @media #{ $desktop } {
    transition-duration: 500ms;
  }
}
.cust-slide-left-leave-active {
  transform: translate(-100vw, 0);
  @media #{ $desktop } {
    opacity: 0;
    transform: translate(-20vw, 0);
  }
}
article#pool-select.cust-slide-right-enter-from {
  transform: translate(-100vw, 0);
  z-index: 99999;
  @media #{ $desktop } {
    opacity: 0;
    transform: translate(-20vw, 0);
  }
}
article.customizer.cust-slide-right-leave-active {
  z-index: 0;
}
.fade-enter-active {
  z-index: 9 !important;
}
.fade-leave-active {
  transition: opacity 500ms ease-out;
  opacity: 0;
  pointer-events: none;
  z-index: 10 !important;
}
</style>